// The owner's script review (E31-S6-T3 first cut, with the scripts list of E31-S6-T1; ADR-033), ported
// from mockups/script-review.html (built from the kit for T0): the titles in the production plan, each
// with the state of its current version, and beside them (under them on a narrow screen) the review of
// the one picked.
//
// The titles are the saved plan's, in its order (GET /v1/production-plan); each one's state is its
// versions list (GET /v1/content/titles/:titleId/versions); the review reads the current version in
// full (GET /v1/content/versions/:id). Held is the plan summary's open contradictions for the title
// (ADR-031). Nothing is sorted or decided here beyond which version a row speaks for.
import type { Principal } from "@waypoint/contracts";
import { Button, cn, focusRing } from "@waypoint/surface";
import { useCallback, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ForbiddenError } from "../../api/errors.js";
import { t } from "../../i18n/t.js";
import { currentOf, isDrafter, stageOf, type VersionSummary } from "../../versions/model.js";
import { useVersion, useVersionLists, usePlan, type Acted } from "../../versions/queries.js";
import { EmptyState, Page } from "../page.js";
import { packName } from "./issue-format.js";
import { mmss } from "./plan-model.js";
import { CARD, ReviewPane } from "./review-pane.js";
import { Seg, StageChip, titleInfoOf, type TitleInfo } from "./script-format.js";

const NARROW = "(max-width: 900px)";

/** A title waits for this approver (not its drafter) while its script is in review and unheld, or its video is ready. */
function waitsForReview(
  current: VersionSummary | undefined,
  info: TitleInfo,
  userId: string,
): boolean {
  if (current === undefined || isDrafter(current, userId)) return false;
  const stage = stageOf(current, info.held);
  return stage === "waiting" || stage === "ready";
}

/** The words for an act the API accepted, spoken once. */
export function announcement(acted: Acted, titleId: string): string {
  switch (acted.act) {
    case "approve":
      return t("review.announce.approved", { id: titleId });
    case "send-back":
      return t("review.announce.sentBack", { id: titleId });
    case "publish":
      return t("review.announce.published", { id: titleId });
  }
}

export function useAnnouncer() {
  const [said, setSaid] = useState("");
  const say = useCallback((message: string) => {
    setSaid("");
    window.setTimeout(() => {
      setSaid(message);
    }, 30);
  }, []);
  const region = (
    <div role="status" aria-live="polite" className="sr-only">
      {said}
    </div>
  );
  return { say, region };
}

function Pane({
  info,
  current,
  listState,
  principal,
  onActed,
}: {
  info: TitleInfo;
  current: VersionSummary | undefined;
  listState: "pending" | "error" | "success";
  principal: Principal;
  onActed: (acted: Acted) => void;
}) {
  const detail = useVersion(current?.id);
  if (listState === "pending" || (current !== undefined && detail.isPending))
    return (
      <p role="status" aria-busy className="text-wp-text-3">
        {t("review.loading")}
      </p>
    );
  if (listState === "error" || detail.isError)
    return (
      <EmptyState icon="alert" title={t("review.failed.title")} line={t("review.failed.line")} />
    );
  if (current === undefined || detail.data === undefined)
    return (
      <div className={cn(CARD, "px-4 py-3.5")}>
        <h2 id="review-title" className="m-0 text-18 font-semibold">
          {info.titleId} · {info.title}
        </h2>
        <p className="mt-2 mb-0 text-13 text-wp-text-2">{t("scripts.noDraft")}</p>
      </div>
    );
  return (
    <ReviewPane
      version={detail.data}
      info={info}
      principal={principal}
      onActed={onActed}
      level={2}
    />
  );
}

export function ScriptsPage({ principal, titleId }: { principal: Principal; titleId?: string }) {
  const plan = usePlan();
  const [, navigate] = useLocation();
  const paneRef = useRef<HTMLElement>(null);
  const { say, region } = useAnnouncer();
  const segments = plan.data?.plan?.segments ?? [];
  const titles = segments.map((s) => titleInfoOf(plan.data, s.titleId, s.packId));
  const lists = useVersionLists(titles.map((info) => info.titleId));
  const currents = lists.map((l) =>
    l.data === undefined ? undefined : currentOf(l.data.versions),
  );

  if (plan.isPending)
    return (
      <Page title={t("scripts.title")}>
        <p role="status" aria-busy className="text-wp-text-3">
          {t("scripts.loading")}
        </p>
      </Page>
    );
  if (plan.isError) {
    const forbidden = plan.error instanceof ForbiddenError;
    return (
      <Page title={t(forbidden ? "page.forbidden.title" : "scripts.failed.title")}>
        <EmptyState
          icon={forbidden ? "lock" : "alert"}
          title={t(forbidden ? "scripts.forbidden" : "scripts.failed.line")}
        >
          {forbidden ? null : (
            <Button
              className="mt-3"
              onClick={() => {
                void plan.refetch();
              }}
            >
              {t("issues.failed.retry")}
            </Button>
          )}
        </EmptyState>
      </Page>
    );
  }

  const forbidden = lists.find((l) => l.error instanceof ForbiddenError);
  if (forbidden !== undefined)
    return (
      <Page title={t("page.forbidden.title")}>
        <EmptyState icon="lock" title={t("scripts.forbidden")} />
      </Page>
    );

  const firstWaiting = titles.findIndex((info, i) =>
    waitsForReview(currents[i], info, principal.userId),
  );
  const index = Math.max(
    0,
    titleId === undefined ? firstWaiting : titles.findIndex((info) => info.titleId === titleId),
  );
  const selected = titles[index];
  const waiting = titles.filter((info, i) =>
    waitsForReview(currents[i], info, principal.userId),
  ).length;
  const packs = [...new Set(titles.map((info) => info.packId))];
  const summary = plan.data.summary;

  const pick = (id: string) => {
    navigate(`/scripts/${encodeURIComponent(id)}`);
    if (window.matchMedia(NARROW).matches)
      requestAnimationFrame(() => {
        paneRef.current?.scrollIntoView({ block: "start" });
      });
  };

  return (
    <section aria-labelledby="page-title" className="grid gap-4">
      <div className="grid gap-1.5">
        <div className="text-12 font-semibold tracking-[0.16em] text-wp-accent uppercase">
          {packs.length === 1 && packs[0] !== undefined && packs[0] !== ""
            ? t("scripts.eyebrow", { pack: packName(packs[0]) })
            : t("scripts.eyebrowPlain")}
        </div>
        <h1 id="page-title" className="text-22 leading-tight font-bold">
          {t("scripts.title")}
        </h1>
        <p className="m-0 max-w-[74ch] text-15 text-wp-text-2">{t("scripts.intro")}</p>
      </div>

      {selected === undefined ? (
        <EmptyState icon="info" title={t("scripts.empty.title")} line={t("scripts.empty.line")} />
      ) : (
        <div className="grid items-start gap-5 pb-14 min-[901px]:grid-cols-[minmax(250px,320px)_minmax(0,1fr)]">
          <section
            aria-labelledby="list-heading"
            className={cn(CARD, "min-w-0 min-[901px]:sticky min-[901px]:top-4")}
          >
            <h2 id="list-heading" className="px-4 pt-3.5 text-14 font-semibold">
              {t("scripts.list")}
            </h2>
            <ul className="m-0 grid list-none gap-1 p-1.5">
              {titles.map((info, i) => {
                const list = lists[i];
                const current = currents[i];
                const isSelected = i === index;
                return (
                  <li key={`${info.packId}/${info.titleId}`}>
                    <button
                      type="button"
                      aria-current={isSelected}
                      onClick={() => {
                        pick(info.titleId);
                      }}
                      className={cn(
                        "grid w-full cursor-pointer grid-cols-[auto_1fr] gap-x-2.5 gap-y-0.5 rounded-8 border px-2.5 py-2 text-left font-sans text-wp-text",
                        isSelected
                          ? "border-wp-accent bg-wp-accent/13"
                          : "border-transparent hover:bg-br-sky/9",
                        focusRing,
                      )}
                    >
                      <span className="row-span-2 mt-0.5 self-start">
                        <Seg id={info.titleId} />
                      </span>
                      <span className="text-14 leading-tight font-semibold">{info.title}</span>
                      <span className="text-12 text-wp-text-3">
                        {list?.isPending === true ? (
                          <span className="sr-only">{t("scripts.loading")}</span>
                        ) : list?.isError === true ? (
                          t("scripts.rowFailed")
                        ) : (
                          <StageChip
                            stage={current === undefined ? "none" : stageOf(current, info.held)}
                            drafter={current !== undefined && isDrafter(current, principal.userId)}
                            {...(info.measured === undefined ? {} : { seconds: info.measured })}
                          />
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="m-0 px-4 pb-3.5 text-12 text-wp-text-3">
              {t("scripts.budget", {
                waiting,
                count: titles.length,
                used: mmss(summary.usedSeconds),
                budget: mmss(summary.budgetSeconds),
              })}
            </p>
          </section>

          <section ref={paneRef} aria-labelledby="review-title" className="min-w-0">
            <Pane
              key={selected.titleId}
              info={selected}
              current={currents[index]}
              listState={lists[index]?.status ?? "pending"}
              principal={principal}
              onActed={(acted) => {
                say(announcement(acted, selected.titleId));
              }}
            />
          </section>
        </div>
      )}
      {region}
    </section>
  );
}
